import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMDescriptionCard,
  PMSmallNumber,
  PMSmallTable,
} from "@pimo/pimo-components";
import {
  getContrastColor,
  getCustomFieldAggregationComponentId,
  getCustomFieldComponentId,
  getCustomFieldPublicId,
} from "product-test-utils";

import { TestAppState } from "../../app";
import { theme } from "../../theme";
import { getDropdownColorValue } from "../../utils/dropdown-options";
import { formatDate } from "../../utils/format-date";

/**
 * The dynamic cards `buildDynamicallyBasedOnAppState` adds on top of the
 * static ones from `./index` (#2222): one per enabled `projectReporting`
 * custom field, plus a sum/average card for any custom field the backend has
 * pre-aggregated (`customFieldSums`/`customFieldAverages`).
 */
export function addCustomFieldCards(
  view: View<TestAppState, DragAndDropLayout>,
  state: {
    configuration?: NonNullable<TestAppState["settings"]>["configuration"];
    project?: NonNullable<TestAppState["currentReport"]>["project"];
  }
) {
  for (const customField of state.configuration?.customFields?.filter(
    (f) => f?.enabled && f?.purpose === "projectReporting"
  ) ?? []) {
    const matchingCustomFieldValue = state.project?.customFields?.find(
      (fieldValue) =>
        getCustomFieldPublicId(fieldValue.customFieldDefinition) ===
        getCustomFieldPublicId(customField)
    );
    switch (customField.type) {
      case "Text": {
        const customFieldCardText = view.addComponent({
          component: PMDescriptionCard,
          layoutProps: {
            componentId: getCustomFieldComponentId(customField),
            description: customField.label,
            title: customField.label,
            labels: [customField.label],
          },
        });

        customFieldCardText.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          description: customField.label,
          text: matchingCustomFieldValue?.value ?? "",
        }));
        break;
      }
      case "Number": {
        const customFieldCard = view.addComponent({
          component: PMSmallNumber,
          layoutProps: {
            componentId: getCustomFieldComponentId(customField),
            labels: [customField.label, "summaryDashboard"],
            description: customField.label,
            title: customField.label,
          },
        });

        customFieldCard.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          number: Number(matchingCustomFieldValue?.value) ?? 0,
        }));
        break;
      }
      case "Date": {
        const customFieldDate = view.addComponent({
          component: PMSmallTable,
          layoutProps: {
            componentId: customField.referenceId
              ? getCustomFieldComponentId(customField)
              : String(customField.id ?? ""),
            labels: [customField.label],
            description: customField.label,
            title: customField.label,
          },
        });

        customFieldDate.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          items: [
            {
              label: customField.label,
              value: matchingCustomFieldValue?.value
                ? formatDate(matchingCustomFieldValue?.value)
                : "n/a",
              chipColor: theme.palette.secondary.main,
              chipTextColor: theme.palette.primary.main,
            },
          ],
          fixedHeight: "134px",
        }));
        break;
      }
      case "Select": {
        const customFieldSelect = view.addComponent({
          component: PMSmallTable,
          layoutProps: {
            componentId: getCustomFieldComponentId(customField),
            labels: [customField.label],
            description: customField.label,
            title: customField.label,
          },
        });

        customFieldSelect.mapState(() => ({
          showInfoIcon: false,
          showMenuIcon: false,
          title: customField.label,
          items: [
            {
              value: matchingCustomFieldValue?.value ?? "n/a",
              chipColor: getDropdownColorValue(
                customField.options,
                matchingCustomFieldValue?.value,
                "#E1F1E7"
              ),
              chipTextColor: getContrastColor(
                getDropdownColorValue(
                  customField.options,
                  matchingCustomFieldValue?.value,
                  "#E1F1E7"
                ),
                theme.palette.text.primary
              ),
            },
          ],
          fixedHeight: "134px",
        }));
        break;
      }
    }
  }

  const customFieldSums = state.project?._calculated?.customFieldSums;

  for (const [recordId, sumValue] of Object.entries(customFieldSums ?? {})) {
    const matchingCustomFieldDefinition =
      state.configuration?.customFields?.find(
        (customField) =>
          getCustomFieldPublicId(customField) === String(recordId)
      );

    if (!matchingCustomFieldDefinition) {
      continue;
    }

    const customFieldSumTextCard = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: getCustomFieldAggregationComponentId(
          "report-sum",
          matchingCustomFieldDefinition
        ),
        labels: [matchingCustomFieldDefinition.label, "customFieldSums"],
        title: `Sum of ${matchingCustomFieldDefinition.label}`,
        description: `Project sum for ${matchingCustomFieldDefinition.label}`,
      },
    });

    customFieldSumTextCard.mapState(() => ({
      showInfoIcon: false,
      showMenuIcon: false,
      title: `Sum of ${matchingCustomFieldDefinition.label}`,
      description: `Project sum for ${matchingCustomFieldDefinition.label}`,
      number: Number(sumValue) ?? 0,
    }));
  }

  for (const [recordId, averageValue] of Object.entries(
    state.project?._calculated?.customFieldAverages ?? {}
  )) {
    const matchingCustomFieldDefinition =
      state.configuration?.customFields?.find(
        (customField) =>
          getCustomFieldPublicId(customField) === String(recordId)
      );

    if (!matchingCustomFieldDefinition) {
      continue;
    }

    const customFieldAverageTextCard = view.addComponent({
      component: PMSmallNumber,
      layoutProps: {
        componentId: getCustomFieldAggregationComponentId(
          "report-average",
          matchingCustomFieldDefinition
        ),
        labels: [matchingCustomFieldDefinition.label, "customFieldAverages"],
        description: `Project average for ${matchingCustomFieldDefinition.label}`,
        title: `Project average for ${matchingCustomFieldDefinition.label}`,
      },
    });

    customFieldAverageTextCard.mapState(() => ({
      showInfoIcon: false,
      showMenuIcon: false,
      title: `Average of ${matchingCustomFieldDefinition.label}`,
      description: `Project average for ${matchingCustomFieldDefinition.label}`,
      number: Number(averageValue) ?? 0,
      roundToDecimals: 2,
    }));
  }
}
